export const SYSTEM_EVIDENCE_MODES = [
  'SIMULATED',
  'LIVE',
  'REPLAYED',
  'HISTORICAL',
] as const;

export type SystemEvidenceMode =
  typeof SYSTEM_EVIDENCE_MODES[number];

export const SYSTEM_HEALTH_STATES = [
  'healthy',
  'degraded',
  'blocked',
  'unknown',
] as const;

export type SystemHealthState =
  typeof SYSTEM_HEALTH_STATES[number];

export const SYSTEM_STATUS_LIGHTS = [
  'green',
  'orange',
  'red',
  'blue',
  'purple',
  'gray',
] as const;

export type SystemStatusLight =
  typeof SYSTEM_STATUS_LIGHTS[number];

export type SystemStatusSignals = {
  criticalFailure: boolean;
  simulationRunning: boolean;
  repairRunning: boolean;
  currentEvidenceAvailable: boolean;
  nonCriticalIssue: boolean;
  reason: string;
};

export type SystemStatusProjection = {
  systemId: string;
  light: SystemStatusLight;
  label:
    | 'Healthy'
    | 'Degraded'
    | 'Blocked'
    | 'Simulating'
    | 'Repairing'
    | 'Unknown';
  reason: string;
  blockingCertificationKeys: string[];
  generatedAt: string;
};

export const CERTIFICATION_STATES = [
  'not_run',
  'pass',
  'fail',
  'pending',
  'not_applicable',
] as const;

export type CertificationState =
  typeof CERTIFICATION_STATES[number];

export type AiActionLevel =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5;

export type SystemAuthoritySource = {
  id: string;
  kind:
    | 'frontend'
    | 'edge_function'
    | 'database_table'
    | 'database_rpc'
    | 'external_service'
    | 'runtime_engine'
    | 'policy'
    | 'documentation';
  label: string;
  reference: string;
  authoritative: boolean;
};

export type SystemDependency = {
  systemId: string;
  relationship:
    | 'required'
    | 'upstream'
    | 'downstream'
    | 'shared';
  reason: string;
};

export type SystemCertificationMatrix = {
  source: CertificationState;
  build: CertificationState;
  simulatedRuntime: CertificationState;
  deployedRuntime: CertificationState;
  liveTransaction: CertificationState;
  recovery: CertificationState;
};

export type SystemEvidenceRecord = {
  id: string;
  systemId: string;
  mode: SystemEvidenceMode;
  category:
    | 'authority'
    | 'runtime'
    | 'simulation'
    | 'certification'
    | 'incident'
    | 'repair'
    | 'dependency';
  source: string;
  summary: string;
  observedAt: string;
  critical: boolean;
  passed: boolean | null;
  correlationId: string | null;
  payload: Record<string, unknown>;
};

export type SystemEvidenceProjection = {
  systemId: string;
  records: SystemEvidenceRecord[];
  latestObservedAt: string | null;
  currentEvidenceAvailable: boolean;
  criticalFailureCount: number;
  failedEvidenceCount: number;
  simulatedEvidenceCount: number;
  liveEvidenceCount: number;
};

export type SystemCertificationProjection = {
  systemId: string;
  certification: SystemCertificationMatrix;
  passedKeys: string[];
  failedKeys: string[];
  pendingKeys: string[];
  notRunKeys: string[];
  requiredPassCount: number;
  completedPassCount: number;
};

export type SystemSimulatorPolicy = {
  enabled: boolean;
  productionWritesAllowed: boolean;
  requiresExplicitLiveApproval: boolean;
  evidenceNamespace: string;
  supportedModes: SystemEvidenceMode[];
  resetRequired: boolean;
};

export type SystemAiPolicy = {
  enabled: boolean;
  maximumAutonomousLevel: AiActionLevel;
  allowedLevels: AiActionLevel[];
  mayReadEvidence: boolean;
  mayGenerateScenarios: boolean;
  mayRecommendRepairs: boolean;
  mayPrepareChanges: boolean;
  mayExecuteProductionWrites: boolean;
  mustCiteEvidence: boolean;
  constitutionOverridesRecommendations: boolean;
};

export const SYSTEM_AI_ASSISTANCE_MODES = [
  'explain',
  'investigate',
  'generate_scenario',
  'recommend_repair',
  'prepare_change',
] as const;

export type SystemAiAssistanceMode =
  typeof SYSTEM_AI_ASSISTANCE_MODES[number];

export const SYSTEM_AI_CONFIDENCE_LEVELS = [
  'low',
  'medium',
  'high',
] as const;

export type SystemAiConfidenceLevel =
  typeof SYSTEM_AI_CONFIDENCE_LEVELS[number];

export type SystemAiEvidenceReference = {
  evidenceId: string;
  systemId: string;
  mode: SystemEvidenceMode;
  source: string;
  observedAt: string;
  summary: string;
};

export type SystemAiAssistanceRequest = {
  requestId: string;
  systemId: string;
  mode: SystemAiAssistanceMode;
  requestedLevel: AiActionLevel;
  question: string;
  evidence: SystemEvidenceRecord[];
  scenarioId: string | null;
  incidentId: string | null;
  requestedAt: string;
};

export type SystemAiRepairRecommendation = {
  recommendationId: string;
  systemId: string;
  title: string;
  summary: string;
  repairType: string;
  risk:
    | 'low'
    | 'medium'
    | 'high'
    | 'prohibited';
  reversible: boolean;
  idempotent: boolean;
  simulationRequired: boolean;
  approvalRequired: boolean;
  productionExecutionAllowed: boolean;
  verificationRequirements: string[];
  rollbackRequirements: string[];
};

export type SystemAiPreparedChange = {
  changeId: string;
  systemId: string;
  title: string;
  summary: string;
  affectedFiles: string[];
  affectedAuthorities: string[];
  productionMutation: boolean;
  executable: false;
  verificationRequirements: string[];
  rollbackPlan: string[];
};

export type SystemAiAssistanceResponse = {
  requestId: string;
  systemId: string;
  mode: SystemAiAssistanceMode;
  actionLevel: AiActionLevel;
  summary: string;
  findings: string[];
  confidence: SystemAiConfidenceLevel;
  confidenceReason: string;
  evidenceReferences: SystemAiEvidenceReference[];
  missingEvidence: string[];
  assumptions: string[];
  scenarioProposal: SystemScenarioDefinition | null;
  repairRecommendation:
    SystemAiRepairRecommendation | null;
  preparedChange: SystemAiPreparedChange | null;
  productionExecutionAttempted: false;
  constitutionApplied: boolean;
  generatedAt: string;
};

export type SystemAiAssistantDefinition = {
  id: string;
  systemId: string;
  name: string;
  description: string;
  supportedModes: SystemAiAssistanceMode[];
  maximumLevel: AiActionLevel;
};

export type SystemAiAssistantHandlerContext = {
  request: SystemAiAssistanceRequest;
  workspace: SystemWorkspaceDefinition;
  now: () => string;
};

export type SystemAiAssistantHandler = {
  assistantId: string;
  systemId: string;
  respond: (
    context: SystemAiAssistantHandlerContext,
  ) => Promise<SystemAiAssistanceResponse>;
};

export type SystemAiAssistantRegistration = {
  definition: SystemAiAssistantDefinition;
  handler: SystemAiAssistantHandler;
};

export const SYSTEM_REPAIR_RISK_LEVELS = [
  'low',
  'medium',
  'high',
  'prohibited',
] as const;

export type SystemRepairRiskLevel =
  typeof SYSTEM_REPAIR_RISK_LEVELS[number];

export const SYSTEM_REPAIR_EXECUTION_STATES = [
  'rejected',
  'approved',
  'executing',
  'verifying',
  'rolling_back',
  'passed',
  'failed',
  'rolled_back',
] as const;

export type SystemRepairExecutionState =
  typeof SYSTEM_REPAIR_EXECUTION_STATES[number];

export type SystemRepairApproval = {
  approvalId: string;
  approvedBy: string;
  approvedAt: string;
  expiresAt: string;
  approvedRepairId: string;
  approvedSystemId: string;
  approvedInputHash: string;
  maximumActionLevel: 4;
};

export type SystemRepairDefinition = {
  id: string;
  systemId: string;
  name: string;
  description: string;
  risk: SystemRepairRiskLevel;
  actionLevel: 4 | 5;
  reversible: boolean;
  idempotent: boolean;
  simulationRequired: boolean;
  approvalRequired: boolean;
  autonomousCertificationRequired: boolean;
  productionMutation: boolean;
  prohibitedSideEffects: string[];
  verificationRequirements: string[];
  rollbackRequirements: string[];
};

export type SystemRepairRequest = {
  requestId: string;
  repairId: string;
  systemId: string;
  requestedActionLevel: 4 | 5;
  inputs: Record<string, unknown>;
  inputHash: string;
  evidence: SystemEvidenceRecord[];
  simulationRunIds: string[];
  approval: SystemRepairApproval | null;
  requestedAt: string;
};

export type SystemRepairHandlerContext = {
  request: SystemRepairRequest;
  definition: SystemRepairDefinition;
  workspace: SystemWorkspaceDefinition;
  signal: AbortSignal;
  now: () => string;
};

export type SystemRepairExecutionOutput = {
  changed: boolean;
  idempotencyKey: string;
  summary: string;
  evidence: SystemEvidenceRecord[];
  productionMutationOccurred: boolean;
};

export type SystemRepairVerificationOutput = {
  passed: boolean;
  summary: string;
  evidence: SystemEvidenceRecord[];
};

export type SystemRepairRollbackOutput = {
  completed: boolean;
  summary: string;
  evidence: SystemEvidenceRecord[];
};

export type SystemRepairHandler = {
  repairId: string;
  systemId: string;
  execute: (
    context: SystemRepairHandlerContext,
  ) => Promise<SystemRepairExecutionOutput>;
  verify: (
    context: SystemRepairHandlerContext,
    output: SystemRepairExecutionOutput,
  ) => Promise<SystemRepairVerificationOutput>;
  rollback: (
    context: SystemRepairHandlerContext,
    output: SystemRepairExecutionOutput,
    verification:
      SystemRepairVerificationOutput | null,
  ) => Promise<SystemRepairRollbackOutput>;
};

export type SystemRepairRegistration = {
  definition: SystemRepairDefinition;
  handler: SystemRepairHandler;
};

export type SystemRepairRunRecord = {
  runId: string;
  requestId: string;
  repairId: string;
  systemId: string;
  actionLevel: 4 | 5;
  state: SystemRepairExecutionState;
  startedAt: string;
  completedAt: string;
  approved: boolean;
  executed: boolean;
  verified: boolean;
  verificationPassed: boolean;
  rollbackAttempted: boolean;
  rollbackCompleted: boolean;
  productionMutationOccurred: boolean;
  evidenceIds: string[];
  error: string | null;
};

export type SystemWorkspaceDefinition = {
  id: string;
  name: string;
  description: string;
  owningGovernor: string;
  authoritySummary: string;
  health: SystemHealthState;
  statusSignals: SystemStatusSignals;
  authoritySources: SystemAuthoritySource[];
  dependencies: SystemDependency[];
  certification: SystemCertificationMatrix;
  simulatorPolicy: SystemSimulatorPolicy;
  aiPolicy: SystemAiPolicy;
  milestone: {
    current: string;
    completed: string[];
    pending: string[];
  };
};

export type SystemScenarioExpectation = {
  id: string;
  description: string;
  required: boolean;
};

export type SystemScenarioDefinition = {
  id: string;
  systemId: string;
  name: string;
  description: string;
  mode: SystemEvidenceMode;
  destructive: boolean;
  productionWrite: boolean;
  inputs: Record<string, unknown>;
  expectations: SystemScenarioExpectation[];
};

export type SystemScenarioResult = {
  scenarioId: string;
  systemId: string;
  mode: SystemEvidenceMode;
  startedAt: string;
  completedAt: string;
  passed: boolean;
  expected: SystemScenarioExpectation[];
  actual: Record<string, unknown>;
  evidenceIds: string[];
  cleanupCompleted: boolean;
  productionMutationOccurred: boolean;
};

export const SYSTEM_SCENARIO_EXECUTION_STATES = [
  'queued',
  'validating',
  'running',
  'cleaning_up',
  'passed',
  'failed',
  'timed_out',
  'rejected',
] as const;

export type SystemScenarioExecutionState =
  typeof SYSTEM_SCENARIO_EXECUTION_STATES[number];

export type SystemScenarioExecutionContext = {
  runId: string;
  scenario: SystemScenarioDefinition;
  workspace: SystemWorkspaceDefinition;
  startedAt: string;
  signal: AbortSignal;
  emitEvidence: (
    record: SystemEvidenceRecord,
  ) => void;
  now: () => string;
};

export type SystemScenarioHandlerOutput = {
  passed: boolean;
  actual: Record<string, unknown>;
  evidence: SystemEvidenceRecord[];
  productionMutationOccurred: boolean;
};

export type SystemScenarioCleanupContext = {
  runId: string;
  scenario: SystemScenarioDefinition;
  workspace: SystemWorkspaceDefinition;
  output: SystemScenarioHandlerOutput | null;
  error: Error | null;
  signal: AbortSignal;
};

export type SystemScenarioHandler = {
  scenarioId: string;
  systemId: string;
  execute: (
    context: SystemScenarioExecutionContext,
  ) => Promise<SystemScenarioHandlerOutput>;
  cleanup?: (
    context: SystemScenarioCleanupContext,
  ) => Promise<void>;
};

export type SystemScenarioRegistration = {
  scenario: SystemScenarioDefinition;
  handler: SystemScenarioHandler;
};

export type SystemScenarioRunRecord = {
  runId: string;
  scenarioId: string;
  systemId: string;
  mode: SystemEvidenceMode;
  state: SystemScenarioExecutionState;
  startedAt: string;
  completedAt: string | null;
  passed: boolean;
  timedOut: boolean;
  rejected: boolean;
  error: string | null;
  expected: SystemScenarioExpectation[];
  actual: Record<string, unknown>;
  evidenceIds: string[];
  cleanupRequired: boolean;
  cleanupAttempted: boolean;
  cleanupCompleted: boolean;
  productionMutationOccurred: boolean;
};

export function assertControlledScenario(
  scenario: SystemScenarioDefinition,
  workspace: SystemWorkspaceDefinition,
): void {
  if (scenario.systemId !== workspace.id) {
    throw new Error(
      'Scenario system does not match workspace authority.',
    );
  }

  if (
    scenario.productionWrite &&
    !workspace.simulatorPolicy.productionWritesAllowed
  ) {
    throw new Error(
      'Production writes are prohibited by this simulator policy.',
    );
  }

  if (
    !workspace.simulatorPolicy.supportedModes.includes(
      scenario.mode,
    )
  ) {
    throw new Error(
      `Unsupported evidence mode: ${scenario.mode}`,
    );
  }
}
